import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDistance } from 'date-fns';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AlertService } from 'src/core/services/alert.service';
import { CompanyService } from 'src/core/services/company.service';
import { SageService } from 'src/core/services/sage.service';
import { DirtinessService } from 'src/core/services/dirtiness.service';
import { IndividualService } from 'src/core/services/individual.service';
import { LoaderService } from 'src/core/services/loader.service';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { UtilsService } from 'src/core/services/utils.service';
import { PermissionsService } from 'src/core/services/permissions.service';
import { all_countries, Country } from 'src/form/components/data/countries';
import { all_provinces, Province } from 'src/form/components/data/provinces';
import { Address, AddressEditPayload } from 'src/interfaces/address';
import { Company, CompanyType } from 'src/interfaces/company';
import { DeliveryType } from 'src/interfaces/delivery-type';
import { IndividualEditPayload, Individual, IndividualType } from 'src/interfaces/individual';

@Component({
  selector: 'core-individual-view',
  templateUrl: './individual-view.component.html',
  styleUrls: ['./individual-view.component.scss']
})
export class IndividualViewComponent implements OnInit {

  countries: Country[] = all_countries;
  provinces: Province[] = all_provinces;
  filteredCountries: Observable<Country[]>;
  deliveryTypes: DeliveryType[] = [];
  companyTypes: CompanyType[] = [];
  individualTypes: IndividualType[] = [];
  companies: Company[] = [];
  selectedCompany: Company | null = null;
  filteredCountriesForIndividual: Observable<Country[]>;

  individualDetailsForm = this._formBuilder.group({
    title: ['', Validators.required],
    initials: ['', Validators.required],
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    gender: ['', Validators.required],
    isAnimal: ['', Validators.required],
    dateOfBirth: ['', Validators.required],
    phoneNumber: ['', Validators.compose([Validators.required, Validators.pattern(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)])],
    phoneNumberAlt: [''],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    numberOfDependants: ['', Validators.compose([Validators.min(0)])],
    individualType: ['', Validators.required],
    companyName: [''],
    companyId: ['']
  });


  addressDetailsForm = this._formBuilder.group({
    country: ['South Africa', Validators.required],
    province: ['', Validators.required],
    city: [''],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    buildingAptNumber: ['', Validators.required],
    postcode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{4,5}$/)])],
  });

  isLoading: boolean = false;
  editMode: boolean = false;
  /** POST /sage/contacts in progress */
  addingToSage: boolean = false;

  individual: Individual | null = null;
  company: Company | null = null;
  address: Address | null = null;
  companyAddress: Address | null = null;

  constructor(
    private _prescriptionService: PrescriptionService,
    private _formBuilder: FormBuilder,
    private _loaderService: LoaderService,
    private _alertService: AlertService,
    private _individualService: IndividualService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _dirtinessService: DirtinessService,
    public utilService: UtilsService,
    private _companyService: CompanyService,
    private _sageService: SageService,
    public readonly perm: PermissionsService
  ) { }

  ngOnInit(): void {

    this
      ._route
      .data
      .subscribe(data => {
        this.individual = data.individual.individual;
        this.company = data.individual.company;
        this.updateView(this.individual, this.company);
      })

    this
      ._prescriptionService
      .getFormData([
        'company_type',
        'delivery_type',
        'individual_type'
      ])
      .subscribe(data => {
        this.deliveryTypes = data.delivery_type;
        this.companyTypes = data.company_type;
        this.individualTypes = data.individual_type;
      });

    this
      .individualDetailsForm
      .controls['companyName']
      .valueChanges
      .pipe(
        startWith(''),
      )
      .subscribe(value => {
        (async () => {
          this.companies = await this._individualService
            .getCompanies(1, 15, value);
        })();
      });

    this.individualDetailsForm
      .valueChanges
      .subscribe(value => {
        this._dirtinessService.setDirty(true);
      })

    this
      ._loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this.filteredCountriesForIndividual = this
      .addressDetailsForm
      .controls['country']
      .valueChanges
      .pipe(
        startWith('South Africa'),
        map(value => this._filterCountries(value))
      );

    this.individualDetailsForm.get("phoneNumberAlt")?.valueChanges
      .subscribe(value => {
        if (!value) this.individualDetailsForm
          .get("phoneNumberAlt")?.clearValidators();

        if (value.length === 0)
          this.individualDetailsForm
            .get("phoneNumberAlt")?.clearValidators();

        if (value.length > 0)
          this.individualDetailsForm
            .get("phoneNumberAlt")?.setValidators([
              Validators.pattern(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)
            ]);
      })

  }

  updateView(individual: Individual | null, company: Company | null) {

    this.selectedCompany = company;

    if (!individual) return;

    this.address = individual.addresses[0];

    if (individual.company) {
      this.companyAddress = individual.company.addresses.length > 0 ?
        individual.company.addresses[0] : null;
    }

    if (this.address) {
      this.addressDetailsForm
        .patchValue({
          country: this.address.country,
          province: this.address.province,
          city: this.address.city,
          addressLine1: this.address.address_1,
          addressLine2: this.address.address_2,
          buildingAptNumber: this.address.building_apt_number,
          postcode: this.address.postcode
        })
    }

    this.individualDetailsForm.patchValue({
      title: individual.title,
      initials: individual.initials,
      firstName: individual.first_name,
      lastName: individual.last_name,
      gender: individual.gender,
      dateOfBirth: individual.date_of_birth,
      phoneNumber: individual.phone_number,
      phoneNumberAlt: individual.phone_number_alt,
      email: individual.email,
      emailAlt: individual.email_alt,
      individualType: individual.individual_type_id,
      numberOfDependants: individual.dependant_number,
      companyId: individual.company_id,
      companyName: company ? company.name : '',
      isAnimal: individual.is_animal.toString(),
    });


    // only do this if alternative phone was provide

    if (individual.phone_number_alt) {
      this.individualDetailsForm.get("phoneNumberAlt")?.setValidators([Validators.pattern(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/)])
    }

    this._dirtinessService.setDirty(false);

  }

  getIndividualType(individual: Individual | null) {
    if (!individual) return "";

    if (!individual.individual_type_id) return "Not Applicable";

    const individualType: IndividualType | undefined = this.individualTypes
      .find(type => type.id === individual.individual_type_id);

    return individualType ? individualType.type : "";
  }

  async save() {

    if (!this.individual) {
      this._alertService.errorAlert("An error happened could not load individual");
      return;
    }

    const isAnimal: boolean = Boolean(parseInt(
      this.individualDetailsForm.value.isAnimal,
    ));

    const addressPayload: AddressEditPayload = {
      building_apt_number: this.addressDetailsForm.value.buildingAptNumber,
      address_1: this.addressDetailsForm.value.addressLine1,
      address_2: this.addressDetailsForm.value.addressLine2,
      province: this.addressDetailsForm.value.province,
      postcode: this.addressDetailsForm.value.postcode,
      city: this.addressDetailsForm.value.city,
      country: this.addressDetailsForm.value.country,
      id: this.address ? this.address.id : undefined
    };

    const payload: IndividualEditPayload = {
      first_name: this.individualDetailsForm.value.firstName,
      last_name: this.individualDetailsForm.value.lastName,
      gender: this.individualDetailsForm.value.gender,
      title: this.individualDetailsForm.value.title,
      initials: this.individualDetailsForm.value.initials,
      dependant_number: parseInt(this.individualDetailsForm.value.numberOfDependants),
      date_of_birth: this.individualDetailsForm.value.dateOfBirth,
      phone_number: this.individualDetailsForm.value.phoneNumber,
      phone_number_alt: this.individualDetailsForm.value.phoneNumberAlt,
      individual_type_id: this.individualDetailsForm.value.individualType ? parseInt(this.individualDetailsForm.value.individualType) : undefined,
      company_id: this.selectedCompany ? this.selectedCompany.id : undefined,
      is_animal: isAnimal,
      email: this.individualDetailsForm.value.email,
      email_alt: this.individualDetailsForm.value.emailAlt,
      address: addressPayload,
      id: undefined,
      profile_number: undefined,
      address_id: undefined
    }

    const success = await this._individualService
      .editIndividual(payload, this.individual.id);

    if (success) {
      this._dirtinessService.setDirty(false);
      await this.refresh();
    }

  }

  companySelected(event: MatAutocompleteSelectedEvent) {
    this.selectedCompany = event.option.value;

    if (!this.selectedCompany) return;
    this
      .individualDetailsForm
      .patchValue({
        companyName: this.selectedCompany.name,
        companyId: this.selectedCompany.id
      });

  }

  async refresh() {
    if (!this.individual) return;

    this.individual = await this._individualService.getIndividual(this.individual.id);

    if (this.individual && this.individual.company_id)
      this.company = await this._individualService.getCompany(this.individual.company_id);

    this.updateView(this.individual, this.company);
  }

  _filterCountries(value: string) {
    return this
      .countries
      .filter((country) => {
        return country
          .name
          .toLowerCase()
          .includes(
            value.toLowerCase()
          )
      })
  }

  formatDateOfBirth(individual: Individual | null) {
    if (!individual) return "";
    return new Date(individual.date_of_birth).toDateString();
  }

  formatTime(time: string | undefined) {
    if (!time) return "-";
    return formatDistance(new Date(time), new Date(), { addSuffix: true });
  }

  getCompanyType(company: Company | null) {
    if (!company) return "-";
    const companyType: CompanyType | undefined = this.companyTypes
      .find(type => company.company_type_id === type.id);

    return companyType ? companyType.type : '-';
  }

  async openSearchCompanyDialog() {
    this.selectedCompany = await this._companyService
      .openCompanySearchDialog();

  }

  deselectCompany() {
    this.selectedCompany = null;
  }

  /** Linked to a Sage contact when GET returns sage_contact_id (normalized in IndividualService). */
  get isOnSage(): boolean {
    return !!(
      this.individual?.sage_contact_id &&
      String(this.individual.sage_contact_id).trim()
    );
  }

  /** Create Sage person contact for this individual; merge id if GET lags behind POST (same as company view). */
  async addToSage(): Promise<void> {
    if (!this.individual || this.addingToSage) return;
    this.addingToSage = true;
    let createdSageId: string | undefined;
    try {
      const result = await this._sageService.createContactForIndividual(
        this.individual
      );
      if (result?.success) {
        createdSageId = result.sage_contact_id;
        if (createdSageId) {
          this.individual = {
            ...this.individual!,
            sage_contact_id: createdSageId,
          };
        }
      }
      const updated = await this._individualService.getIndividual(
        this.individual!.id
      );
      if (updated) {
        const merged =
          updated.sage_contact_id?.trim() ||
          createdSageId ||
          this.individual?.sage_contact_id;
        this.individual =
          merged != null && String(merged).trim()
            ? { ...updated, sage_contact_id: String(merged) }
            : updated;
      } else if (createdSageId) {
        this.individual = { ...this.individual!, sage_contact_id: createdSageId };
      }
    } finally {
      this.addingToSage = false;
    }
  }

  // Navigate back to individual list preserving search and type
  backToList() {
    const qp = this._route.snapshot.queryParamMap;
    const search = qp.get('search') || undefined;
    const individualType = qp.get('individualType') || undefined;
    this._router.navigate(['/portal/individuals'], {
      queryParams: { search, individualType }
    });
  }

}
